import { useRef, useState, useCallback, useEffect } from 'react';

declare global {
  interface Window {
    SpeechRecognition: new () => SR;
    webkitSpeechRecognition: new () => SR;
  }
}
interface SR {
  continuous: boolean; interimResults: boolean; lang: string;
  start(): void; stop(): void; abort(): void;
  onresult: ((e: SREvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
}
interface SREvent { results: SpeechRecognitionResultList; }

export type RecorderStatus = 'inactive' | 'listening' | 'speechActive';

export interface UseVoiceRecorderReturn {
  status: RecorderStatus;
  supported: boolean;
  segments: string[];
  detectedLang: 'en-US' | 'de-DE';
  fatalError: boolean;
  start: () => void;
  stop: () => Promise<{ segments: string[]; lang: 'en-US' | 'de-DE' }>;
}

const DE_WORDS = new Set(['der','die','das','und','ist','hat','ein','eine','nicht','mit','für','auf','von','zu','den','dem','sich','auch','noch','war','bei','nach','wie','über','nur','aber','als','wenn','oder','wir','sie','er','ich','habe','haben','kann','sind','wird','sehr','gut','mehr','schon','immer','wieder','dass','weil','also','ganz','wurde','werden','durch','müssen','können','soll','sollte','waren']);

function detectLang(text: string): 'en-US' | 'de-DE' {
  if (/[äöüßÄÖÜ]/.test(text)) return 'de-DE';
  const words = text.toLowerCase().replace(/[^\p{L}\s]/gu, '').split(/\s+/).filter(Boolean);
  if (!words.length) return 'en-US';
  let hits = 0;
  for (const w of words) if (DE_WORDS.has(w)) hits++;
  return (hits / words.length) >= 0.2 ? 'de-DE' : 'en-US';
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const SRClass = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : undefined;
  const supported = !!SRClass;
  const [status, setStatus] = useState<RecorderStatus>('inactive');
  const [segments, setSegments] = useState<string[]>([]);
  const [detectedLang, setDetectedLang] = useState<'en-US' | 'de-DE'>('en-US');
  const [fatalError, setFatalError] = useState(false);

  const activeRef = useRef(false);
  const segRef = useRef<string[]>([]);
  const recRef = useRef<SR | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const vadRef = useRef<number | null>(null);
  const spkRef = useRef(false);
  const silRef = useRef(0);
  const langRef = useRef<'en-US' | 'de-DE'>('en-US');
  const resolveStopRef = useRef<((v: { segments: string[]; lang: 'en-US' | 'de-DE' }) => void) | null>(null);

  const sessionRef = useRef(0);

  const cleanup = useCallback(() => {
    if (vadRef.current !== null) { clearInterval(vadRef.current); vadRef.current = null; }
    streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
    if (ctxRef.current && ctxRef.current.state !== 'closed') void ctxRef.current.close();
    ctxRef.current = null; spkRef.current = false; silRef.current = 0;
  }, []);

  useEffect(() => {
    return () => { activeRef.current = false; cleanup(); if (recRef.current) { try { recRef.current.abort(); // eslint-disable-next-line no-empty
} catch { } recRef.current = null; } };
  }, [cleanup]);

  const start = useCallback(() => {
    if (!SRClass || activeRef.current) return;
    activeRef.current = true;
    // A microphone request from an earlier start/stop may settle after this one began: it must
    // not touch this session.
    const session = ++sessionRef.current;
    segRef.current = [];
    setSegments([]);
    setStatus('listening');
    setFatalError(false);
    langRef.current = 'en-US';
    setDetectedLang('en-US');

    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      if (!activeRef.current || session !== sessionRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;
      const ac = new AudioContext(); ctxRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const an = ac.createAnalyser(); an.fftSize = 512; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      vadRef.current = window.setInterval(() => {
        if (!activeRef.current) return;
        an.getByteFrequencyData(buf);
        const rms = buf.reduce((s, v) => s + v, 0) / buf.length;
        if (rms > 14) { silRef.current = 0; if (!spkRef.current) { spkRef.current = true; setStatus('speechActive'); } }
        else if (spkRef.current) { if (!silRef.current) silRef.current = Date.now(); else if (Date.now() - silRef.current > 600) { spkRef.current = false; silRef.current = 0; setStatus('listening'); } }
      }, 60);
    }).catch(() => {
      if (session !== sessionRef.current) return;
      // No microphone (denied, busy, missing): fatal, so the parent leaves recording mode.
      activeRef.current = false;
      cleanup();
      try { recRef.current?.abort(); } catch { /* already stopped */ }
      recRef.current = null;
      setStatus('inactive');
      setFatalError(true);
    });

    const startRec = () => {
      if (!activeRef.current || !SRClass) return;
      const rec = new SRClass();
      rec.continuous = true;
      rec.interimResults = false;
      rec.lang = langRef.current;
      rec.onresult = (e: SREvent) => {
        for (let i = 0; i < e.results.length; i++) {
          if (!e.results[i].isFinal) continue;
          const t = e.results[i][0].transcript.trim();
          if (t && !segRef.current.includes(t)) {
            segRef.current.push(t);
            setSegments([...segRef.current]);
            const det = detectLang(t);
            if (det !== langRef.current) { langRef.current = det; setDetectedLang(det); }
          }
        }
      };
      rec.onend = () => {
        recRef.current = null;
        if (activeRef.current) {
          try { startRec(); // eslint-disable-next-line no-empty
} catch { }
        } else if (resolveStopRef.current) {
          resolveStopRef.current({ segments: [...segRef.current], lang: langRef.current });
          resolveStopRef.current = null;
        }
      };
      rec.onerror = (e: { error: string }) => {
        const FATAL = ['not-allowed', 'service-not-allowed', 'audio-capture', 'network'];
        if (FATAL.includes(e.error)) {
          activeRef.current = false;
          cleanup();
          setStatus('inactive');
          setFatalError(true);
          if (resolveStopRef.current) {
            resolveStopRef.current({ segments: [...segRef.current], lang: langRef.current });
            resolveStopRef.current = null;
          }
        }
        // 'no-speech', 'aborted', 'bad-grammar' are non-fatal — onend handles restart
      };
      rec.start();
      recRef.current = rec;
    };
    startRec();
  }, [SRClass, cleanup]);

  const stop = useCallback((): Promise<{ segments: string[]; lang: 'en-US' | 'de-DE' }> => {
    activeRef.current = false;
    cleanup();
    setStatus('inactive');
    if (recRef.current) {
      return new Promise((resolve) => {
        resolveStopRef.current = resolve;
        try { recRef.current!.stop(); } catch { recRef.current = null; resolve({ segments: [...segRef.current], lang: langRef.current }); }
        setTimeout(() => { if (resolveStopRef.current) { resolveStopRef.current({ segments: [...segRef.current], lang: langRef.current }); resolveStopRef.current = null; } }, 500);
      });
    }
    return Promise.resolve({ segments: [...segRef.current], lang: langRef.current });
  }, [cleanup]);

  return { status, supported, segments, detectedLang, fatalError, start, stop };
}
